import React, {
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ApolloError } from "@apollo/client";

import { auth } from "../config/firebase/index";
import { useLocalStorage } from "../hooks/useLocalStorage";
import routeNames from "../constants/route-names";
import localStorageKeys from "../constants/local-storage-keys";
import { useGetUserQuery } from "../graphql/queries/getUser.generated";
import { getApolloClient } from "../config/apollo";
import { Maybe, Users } from "../graphql/generated/graphql";

// Possible values for the login status context value
export enum LoginStatus {
  LOADING = "loading",
  IN = "in",
  OUT = "out",
}

// Token refresh interval (20 minutes) - Firebase token expiration = 60 minutes
// Refresh more frequently to ensure token is always valid
const TOKEN_REFRESH_INTERVAL = 20 * 60 * 1000;

export type AuthContextProps = {
  firebaseAuth: any;
  loggedUser: any;
  setLoggedUser?: (user: any) => void;
  loggedUserData: Maybe<Users>;
  setLoggedUserData?: (user: any) => void;
  login?: (data: { email: string; password: string }) => Promise<boolean>;
  logout?: () => void;
  token: string;
  loginStatus: string;
  updateToken?: (user: any) => Promise<void | null>;
  refreshToken?: () => Promise<void>;
};

/*
We use ContextAPI to obtain the logged-in user, if any,
and pass it to the child components of our application.
*/
export const AuthContext = createContext<AuthContextProps>({
  firebaseAuth: auth,
  loggedUser: null,
  loggedUserData: null,
  token: "",
  loginStatus: LoginStatus.LOADING,
});

// This useAuth Hook exposes the user's state and methods for user login and logout.
export const useAuth = () => {
  return useContext(AuthContext);
};

const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loggedUser, setLoggedUser] = useLocalStorage("loggedUser", null);
  const [loggedUserData, setLoggedUserData] = useState(null);
  const [loginStatus, setLoginStatus] = useState(LoginStatus.LOADING);
  const [token, setToken] = useState("");
  const [firstLoadCompleted, setFirstLoadCompleted] = useState(false);

  const navigate = useNavigate();

  // Function to get and update token
  const updateToken = useCallback(async (user: any) => {
    if (!user) return;
    try {
      // console.log("Refreshing Firebase token...");
      const newToken = await user.getIdToken(true);
      setToken(newToken);
      window.localStorage.setItem(localStorageKeys.TOKEN_KEY, newToken);
      const apolloClient = getApolloClient(true, newToken);
      // Reset Apollo Client's store with new token
      await apolloClient.resetStore();
      // console.log("Token refreshed successfully");
    } catch (error) {
      console.error("Error updating token:", error);
      // If token refresh fails due to network issues, we'll retry on the next interval
      return null;
    }
  }, []);

  // Function to refresh token - can be called manually when needed
  const refreshToken = useCallback(async () => {
    if (!loggedUser) return null;
    try {
      // console.log("Manually refreshing token...");
      const newToken = await loggedUser.getIdToken(true);
      setToken(newToken);
      window.localStorage.setItem(localStorageKeys.TOKEN_KEY, newToken);
      const apolloClient = getApolloClient(true, newToken);
      // Reset Apollo Client's store with new token
      await apolloClient.resetStore();
      // console.log("Token manually refreshed successfully");
      return newToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    }
  }, [loggedUser]);

  const handleOnErrorsGetUser = useCallback(
    (error: ApolloError) => {
      console.log("Error Get Users: ", error);

      // Check if the error is related to authentication
      if (
        error.message.includes("JWT") ||
        error.message.includes("token") ||
        error.message.includes("authentication") ||
        error.message.includes("authorization")
      ) {
        console.warn(
          "Authentication error detected in query, attempting token refresh"
        );
        refreshToken();
      }
    },
    [refreshToken]
  );

  const handleOnCompletedGetUser = useCallback(
    (data: any) => {
      if (data?.users_by_pk) {
        setLoggedUserData(data?.users_by_pk);
      }

      if (firstLoadCompleted) {
        return;
      }

      setFirstLoadCompleted(true);

      const isAdmin = data?.users_by_pk?.isHighnoteAdmin;
      const targetRoute = isAdmin
        ? routeNames.AI_CHATS
        : routeNames.AI_CHATS_NONADMIN;

      // Only redirect if we are on public routes
      const publicRoutes = [routeNames.HOME, routeNames.LOGIN, "/"];
      if (publicRoutes.includes(window.location.pathname)) {
        navigate(targetRoute, { replace: true });
      }
    },
    [navigate, firstLoadCompleted]
  );

  const { data, loading } = useGetUserQuery({
    skip: !loggedUser?.uid,
    variables: {
      id: loggedUser?.uid,
    },
    errorPolicy: "all",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUser,
    onCompleted: handleOnCompletedGetUser,
  });

  // call this function when you want to authenticate the user
  const login = useCallback(
    async (data: { email: string; password: string }) => {
      const { email, password } = data;

      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );

        if (userCredential) {
          // Signed in
          const user = userCredential.user;
          // console.log("Firebase login userCredential: ", userCredential);

          setLoggedUser(user);
          const idToken = await user.getIdToken();
          setToken(idToken);
          window.localStorage.setItem(localStorageKeys.TOKEN_KEY, idToken);
          return true;
        }
        return false;
      } catch (error: any) {
        console.log("Firebase login error: ", error);
        const errorCode = error.code;
        const errorMessage = error.message;
        console.log("Firebase login error.code: ", errorCode);
        console.log("Firebase login errorMessage: ", errorMessage);
        return false;
      }
    },
    [auth, setLoggedUser]
  );

  // call this function to sign out logged in user
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      setLoggedUser(null);
      setLoggedUserData(null);
      setToken("");
      window.localStorage.removeItem(localStorageKeys.TOKEN_KEY);
      setLoginStatus(LoginStatus.OUT);
      const apolloClient = getApolloClient(true);
      await apolloClient.clearStore();
      navigate(routeNames.HOME, { replace: true });
    } catch (error) {
      console.log("Firebase logout error: ", error);
    }
  }, [auth, setLoggedUser, navigate]);

  // Set up periodic token refresh - essential for keeping the session alive
  useEffect(() => {
    if (!loggedUser) return;

    // console.log("Setting up token refresh interval...");

    // Immediately check token validity and refresh if needed
    const initialCheck = async () => {
      try {
        // Try to refresh the token right away to ensure we start with a valid one
        await updateToken(loggedUser);
      } catch (error) {
        console.error("Initial token verification failed:", error);
      }
    };

    initialCheck();

    const refreshTokenInterval = setInterval(() => {
      // console.log("Running scheduled token refresh...");
      updateToken(loggedUser);
    }, TOKEN_REFRESH_INTERVAL);

    // Also refresh token on window focus, which helps with inactive tabs or when returning to the app
    const handleWindowFocus = () => {
      // console.log("Window focused, checking token...");
      updateToken(loggedUser);
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      clearInterval(refreshTokenInterval);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [loggedUser, updateToken]);

  // Set up authentication state listener
  useEffect(() => {
    /*
    Firebase provides the onAuthStateChanged method, which is an observer of the global authentication object.
    This observer is called every time the user's login state changes.
    If the user is logged in, the callback will receive the user's properties as a parameter; otherwise it will receive null.
    */
    // console.log("Setting up Firebase auth state listener...");
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", user ? "User logged in" : "No user");

      if (user) {
        // User is signed in, see docs for a list of available properties
        // https://firebase.google.com/docs/reference/js/auth.user
        const { uid, metadata: firebaseUserMetadata } = user || {};
        const { creationTime, lastSignInTime } = firebaseUserMetadata || {};

        const userIsJustCreated =
          creationTime && lastSignInTime && creationTime === lastSignInTime;

        try {
          // Update the token and store it
          await updateToken(user);

          // Store Firebase user in localStorage
          setLoggedUser(user);

          if (userIsJustCreated) {
            setLoginStatus(LoginStatus.IN);
          } else {
            setLoginStatus(LoginStatus.IN);
          }
        } catch (error) {
          console.error("Error during auth state change handling:", error);
          // If there's an error handling the authentication state, we still try to maintain the session
          setLoginStatus(LoginStatus.IN);
        }
      } else {
        // User is signed out. user === null when user is not logged
        setLoggedUser(null);
        setToken("");
        window.localStorage.removeItem(localStorageKeys.TOKEN_KEY);
        setLoginStatus(LoginStatus.OUT);
      }
    });

    return () => unsubscribe();
  }, [setLoggedUser, updateToken]);

  // Custom event listener for auth navigation events (used by Apollo error handling)
  useEffect(() => {
    const handleAuthNavigation = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { route } = customEvent.detail || {};

      if (route) {
        // console.log(`Auth navigation event received, redirecting to ${route}`);
        navigate(route);
      }
    };

    window.addEventListener("authNavigation", handleAuthNavigation);

    return () => {
      window.removeEventListener("authNavigation", handleAuthNavigation);
    };
  }, [navigate]);

  const value = useMemo(() => {
    return {
      firebaseAuth: auth,
      loggedUser,
      setLoggedUser,
      loggedUserData,
      setLoggedUserData,
      token,
      loginStatus,
      login,
      logout,
      updateToken,
      refreshToken,
    };
  }, [
    loggedUser,
    loggedUserData,
    token,
    loginStatus,
    login,
    logout,
    updateToken,
    refreshToken,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
